import { Button, Col, Input, Row } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';
import React from 'react';
import { connect } from 'react-redux';

import { selectors as authSelectors } from '../../../slices/auth';
import { RootState } from '../../../slices/rootReducer';
import { IUpdateUserPayload, updateUserById } from '../../../slices/settings/profile.thunk';
import { AutoEllipsisTooltip } from '../../common/autoEllipsisTooltip';
import { Avatar } from './avatar';
import { ChangePasswordModal } from './changePasswordModal';

import 'react-intl-tel-input/dist/main.css';
import styles from './index.module.scss';

interface IStateProps {
  firstName: string;
  lastName: string;
  avatar: string;
  email: string;
  id: string;
  accountId: string;
}

interface IDispatchProps {
  updateUserById: (arg: IUpdateUserPayload) => void;
}

interface IState {
  openPasswordModal: boolean;
  image: string;
  isModified: boolean;
}

type Props = IStateProps & IDispatchProps;
class IProfileBase extends React.Component<Props, IState> {
  private formRef: React.RefObject<FormInstance>;
  constructor(props: Props) {
    super(props);
    this.state = {
      openPasswordModal: false,
      image: props.avatar,
      isModified: false
    };
    this.toggleOpenPasswordModal = this.toggleOpenPasswordModal.bind(this);
    this.onSaveChanges = this.onSaveChanges.bind(this);
    this.onChangeImage = this.onChangeImage.bind(this);
    this.handleValuesChange = this.handleValuesChange.bind(this);
    this.formRef = React.createRef<FormInstance>();
  }

  private toggleOpenPasswordModal() {
    this.setState((state) => ({ openPasswordModal: !state.openPasswordModal }));
  }

  private onChangeImage(image: string | null) {
    this.setState({ image: image ?? '', isModified: true });
  }

  private handleValuesChange() {
    this.setState({ isModified: true });
  }

  private renderUserCard() {
    const { firstName, lastName, email } = this.props;
    const { image } = this.state;
    return (
      <div className={styles.userCard}>
        <Avatar
          imageSrc={image}
          initials={`${firstName[0] ?? ''}${lastName[0] ?? ''}`}
          onChangeImage={this.onChangeImage}
        />
        <div>
          <div className={styles.name}><AutoEllipsisTooltip text={firstName} /></div>
          <div className={styles.name}><AutoEllipsisTooltip text={lastName} /></div>
          <div className={styles.email}><AutoEllipsisTooltip text={email} /></div>
        </div>
      </div>
    );
  }

  private renderFields() {
    const { firstName, lastName } = this.props;
    return (
      <Form
        ref={this.formRef}
        initialValues={{ firstName, lastName }}
        className={styles.fields}
        onValuesChange={this.handleValuesChange}
      >
        <Row gutter={[24, 0]}>
          <Col span={12}>
            <Form.Item
              label="First name"
              name="firstName"
              rules={[{ required: true, message: 'First name is required' }]}
            >
              <Input placeholder="First name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Last name"
              name="lastName"
              rules={[{ required: true, message: 'Last name is required' }]}
            >
              <Input placeholder="Last name" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Button type="text" onClick={this.toggleOpenPasswordModal} >
              Change password
            </Button>
          </Col>
        </Row>
      </Form>
    );
  }

  private async onSaveChanges() {
    const currentForm = this.formRef.current;
    await currentForm?.validateFields();
    const { image } = this.state;
    const { id, accountId } = this.props;
    const formValues = currentForm?.getFieldsValue();
    const user: IUpdateUserPayload = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      avatar: image,
      phone: formValues.phone,
      id,
      accountId
    };
    this.props.updateUserById(user);
  }

  render() {
    const { id } = this.props;
    const { openPasswordModal, isModified } = this.state;
    if (!id) {
      return null;
    }

    return (
      <div className={styles.card}>
        <div className={styles.form}>
          {this.renderUserCard()}
          {this.renderFields()}
        </div>
        <div className={styles.actions}>
          <Button
            type="primary"
            onClick={this.onSaveChanges}
            disabled={!isModified}
          >
            Save changes
          </Button>
        </div>
        {openPasswordModal && <ChangePasswordModal onClose={this.toggleOpenPasswordModal} />}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IStateProps => {
  const user = authSelectors.getLoggedInUser(state);
  return {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    avatar: user?.avatar ?? '',
    email: user?.email ?? '',
    id: user?.id ?? '',
    accountId: user?.accountId ?? ''
  };
};

export const Profile = connect(mapStateToProps, { updateUserById })(IProfileBase);